import React, { useEffect, useState } from "react";
import Dialog from "../../../components/Dialog/Dialog";
import DialogTextField from "../../../components/DialogTextField/DialogTextField";
import EditableCompactList from "../../../components/EditableCompactList/EditableCompactList";
import { Privilege } from "../../PrivilegesPage/Models/Privilige";
import {
  getRequest,
  PRIVILEGES_OVERVIEW_ENDPOINT,
  SUB_ROLES_ENDPOINT,
} from "../../../ApiService";
import {
  DESCRIPTION,
  ERROR_MESSAGE,
  ERROR_TITLE,
  NAME,
  PRIVILEGES,
} from "../../../utility/TextLabels";
import { SubRole } from "../Models/SubRole";
import { SubRoleDetails } from "../Models/SubRoleDetails";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";

type SubRoleDialogProps = {
  title: string;
  existingId?: string;
  existingName?: string;
  existingDescription?: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (subRole: SubRole) => void;
};

const SubRoleDialog: React.FC<SubRoleDialogProps> = ({
  title,
  existingId,
  existingName,
  existingDescription,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitValid, setIsSubmitValid] = useState(false);
  const [isErrorMessageOpen, setIsErrorMessageOpen] = useState(false);

  const [selectablePrivileges, setSelectablePrivileges] = useState<Privilege[]>(
    []
  );
  const [selectedPrivilegeIds, setSelectedPrivilegeIds] = useState<string[]>(
    []
  );
  const [preSelectedPrivileges, setPreSelectedPrivilege] = useState<string[]>(
    []
  );

  const getSubRoleDetails = async (id: string) => {
    try {
      await getRequest(SUB_ROLES_ENDPOINT, id).then(
        (subRoleDetails: SubRoleDetails) => {
          setName(subRoleDetails.name);
          setDescription(subRoleDetails.description ?? "");

          const privilegeNames =
            subRoleDetails.privilegeDetails?.map(
              (privilege) => privilege.name
            ) || [];
          setPreSelectedPrivilege(privilegeNames);

          const privilegeIds =
            subRoleDetails.privilegeDetails?.map(
              (privilege) => privilege?.id
            ) || [];
          setSelectedPrivilegeIds(privilegeIds);
        }
      );
    } catch (error) {
      setIsErrorMessageOpen(true);
    }
  };

  useEffect(() => {
    const isNameFieldFilled = name.length > 1;
    setIsSubmitValid(isNameFieldFilled);
  }, [name]);

  useEffect(() => {
    if (!isOpen) return;
    getPrivileges();
    if (existingId) {
      getSubRoleDetails(existingId);
    }
  }, [isOpen, existingId]);

  const getPrivileges = async () => {
    try {
      await getRequest(PRIVILEGES_OVERVIEW_ENDPOINT).then(
        (fetchedPrivileges: Privilege[]) => {
          setSelectablePrivileges(fetchedPrivileges);
        }
      );
    } catch (error) {
      setIsErrorMessageOpen(true);
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDescription(event.target.value);
  };

  const addPrivilegeId = (name: string) => {
    const privilegeToAdd: Privilege | undefined = selectablePrivileges.find(
      (privilege) => privilege.name === name
    );
    if (privilegeToAdd) {
      setSelectedPrivilegeIds([...selectedPrivilegeIds, privilegeToAdd.id!]);
    }
  };

  const removePrivilegeId = (name: string) => {
    const privilegeToRemove: Privilege | undefined = selectablePrivileges.find(
      (privilege) => privilege.name === name
    );

    if (privilegeToRemove) {
      const updatedPrivileges: string[] = selectedPrivilegeIds.filter(
        (privilegeId) => privilegeId !== privilegeToRemove.id
      );
      setSelectedPrivilegeIds(updatedPrivileges);
    }
  };

  const prepareSubmit = () => {
    const subRole: SubRole = {
      name: name,
      description: description,
      privilegeIds: selectedPrivilegeIds,
    };

    onSubmit(subRole);
    prepareClose();
  };

  const prepareClose = () => {
    setName("");
    setDescription("");
    setSelectablePrivileges([]);
    setSelectedPrivilegeIds([]);
    onClose();
  };

  return (
    <>
      <ErrorMessage
        isOpen={isErrorMessageOpen}
        primaryText={ERROR_TITLE}
        additionalText={ERROR_MESSAGE}
        onClose={() => setIsErrorMessageOpen(false)}
      ></ErrorMessage>

      <Dialog
        title={title}
        open={isOpen}
        onClose={prepareClose}
        onSubmit={prepareSubmit}
        isSubmitEnabled={isSubmitValid}
      >
        <DialogTextField
          label={NAME}
          name={existingName}
          inputValue={name}
          onChange={handleNameChange}
        />
        <DialogTextField
          label={DESCRIPTION}
          name={existingDescription}
          rows={3}
          inputValue={description}
          onChange={handleDescriptionChange}
        />
        <EditableCompactList
          label={PRIVILEGES}
          preSelectedItems={preSelectedPrivileges}
          selectableItems={selectablePrivileges}
          onAddingItem={addPrivilegeId}
          onRemovingItem={removePrivilegeId}
        />
      </Dialog>
    </>
  );
};

export default SubRoleDialog;
